import { createFileRoute, useRouter } from '@tanstack/react-router'
import { Eye, EyeOff } from 'lucide-react'
import { useState } from 'react'
import { z } from 'zod'

import { ApiError } from '@/api/client'
import { useLogin } from '@/api/queries'
import { Logo } from '@/components/layout/logo'
import { ThemeToggle } from '@/components/layout/theme'
import { Button, Card, Field, Input } from '@/components/ui/primitives'
import { LOGIN_ERROR_LABELS, loginErrorLabel } from '@/shared/labels'
import { canOpenDashboard } from '@/shared/permissions'

const searchSchema = z.object({
  redirect: z.string().optional(),
  error: z.string().optional(),
})

export const Route = createFileRoute('/login')({
  validateSearch: searchSchema,
  component: LoginPage,
})

function readField(form: FormData, name: string): string {
  const value = form.get(name)
  return typeof value === 'string' ? value : ''
}

function messageFromError(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 429) return LOGIN_ERROR_LABELS.rate_limited
    if (error.isUnauthorized) return LOGIN_ERROR_LABELS.invalid_credentials
  }
  return 'Не удалось войти — попробуйте ещё раз'
}

function LoginPage() {
  const { redirect: redirectTo, error: searchError } = Route.useSearch()
  const router = useRouter()
  const login = useLogin()
  const [showPassword, setShowPassword] = useState(false)
  const [formError, setFormError] = useState<string | null>(loginErrorLabel(searchError))

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setFormError(null)
    const form = new FormData(event.currentTarget)
    login.mutate(
      {
        login: readField(form, 'login'),
        password: readField(form, 'password'),
      },
      {
        onSuccess: (me) => {
          // Роль проверяет и бэкенд на каждом эндпоинте; здесь — чтобы сотрудник
          // не попадал на пустой экран без объяснения.
          if (!canOpenDashboard(me.role)) {
            setFormError(LOGIN_ERROR_LABELS.forbidden_role)
            return
          }
          void router.navigate({ to: redirectTo ?? '/', replace: true })
        },
        onError: (error) => setFormError(messageFromError(error)),
      },
    )
  }

  return (
    <div className="flex min-h-dvh flex-col bg-canvas">
      <div className="flex justify-end p-4">
        <ThemeToggle />
      </div>

      <div className="flex flex-1 items-center justify-center px-4 pb-16">
        <div className="w-full max-w-sm">
          <div className="mb-7 flex flex-col items-center gap-3 text-center">
            <Logo className="size-12" />
            <div>
              <h1 className="font-display text-xl font-bold text-ink">Askona Ассистент</h1>
              <p className="mt-1 text-[13px] text-ink-subtle">
                Речевая аналитика разговоров с клиентами
              </p>
            </div>
          </div>

          <Card className="p-5">
            <form className="space-y-4" onSubmit={handleSubmit} noValidate>
              <Field label="Логин" htmlFor="login">
                <Input
                  id="login"
                  name="login"
                  autoComplete="username"
                  autoFocus
                  required
                  placeholder="Логин"
                />
              </Field>

              <Field label="Пароль" htmlFor="password">
                <div className="relative">
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    required
                    placeholder="Пароль"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((value) => !value)}
                    aria-label={showPassword ? 'Скрыть пароль' : 'Показать пароль'}
                    className="absolute inset-y-0 right-0 flex cursor-pointer items-center px-3 text-ink-subtle transition-colors hover:text-ink"
                  >
                    {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                  </button>
                </div>
              </Field>

              {formError && (
                <p
                  role="alert"
                  className="rounded-[var(--radius-control)] bg-negative-soft px-3 py-2 text-[13px] text-negative"
                >
                  {formError}
                </p>
              )}

              <Button type="submit" variant="primary" loading={login.isPending} className="w-full">
                Войти
              </Button>
            </form>
          </Card>
        </div>
      </div>
    </div>
  )
}
